import {
  ForwardedRef,
  forwardRef,
  ReactElement,
  ReactNode,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Flex } from '@revenuemarket/cds';
import { thematicColors, theme, typographyStyle } from '@revenuemarket/design';
import { IHeaderComp, IHeaderParams, SortDirection } from 'ag-grid-community';
import { Dropdown } from 'antd';

import AGGridSortIcon from './AGGridSortIcon';
import Typography from '../../Typography';

export interface AGGridFilterProps {
  onClose: () => void;
}

interface Props extends IHeaderParams {
  align?: 'left' | 'right' | 'center';
  menu?: ReactElement;
  sortable?: boolean;
  sort?: SortDirection;
  onSortChange?: (sort: SortDirection) => void;
  filter?: (props: AGGridFilterProps) => ReactNode;
  filterApplied?: boolean;
  className?: string;
  color?: string;
  customAttributes?: { [key: string]: any };
}

const sortOrder = ['asc', 'desc', null] as const;

const AGGridHeader = (
  {
    color = theme.colorTokens.textDefault,
    align,
    sortable,
    sort = null,
    column,
    filter,
    filterApplied,
    onSortChange,
    className,
    customAttributes,
  }: Props,
  ref: ForwardedRef<IHeaderComp>,
) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      getGui: () => containerRef.current ?? document.body,
      refresh: () => true,
    }),
    [],
  );

  return (
    <Flex
      ref={containerRef}
      align="center"
      style={{
        width: '100%',
        cursor: sortable ? 'pointer' : undefined,
        height: '100%',
      }}
      className={className}
      onClick={(e) => {
        if (sortable) {
          e.preventDefault();
          const nextSort = sortOrder[(sortOrder.indexOf(sort) + 1) % sortOrder.length];
          onSortChange?.(nextSort);
        }
      }}
      {...customAttributes}
    >
      <Flex style={{ flex: 1 }} justify={align} align="center">
        <Typography
          variant="caption1"
          weight={400}
          color={color}
          style={{
            ...typographyStyle['13_400'],
          }}
        >
          {column.getColDef().headerName}
        </Typography>
      </Flex>
      <Flex gap={4} align="center">
        {sortable && <AGGridSortIcon sortDirection={sort} />}

        {(filter || filterApplied) && (
          <Dropdown
            trigger={['click']}
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
            align={{ points: ['tr', 'cl'] }}
            dropdownRender={() => filter?.({ onClose: () => setIsMenuOpen(false) })}
            destroyPopupOnHide
          >
            <span
              className="ag-icon-filter"
              style={{
                cursor: 'pointer',
                color: filterApplied ? thematicColors.primary.medium : '',
              }}
            />
          </Dropdown>
        )}
      </Flex>
    </Flex>
  );
};

export default forwardRef(AGGridHeader);
